import React, { useMemo } from 'react'

import { ComponentLibrary, useComponentLibrary } from '../hooks'
import { styled } from './styled'

type ComponentLibraryRenderer<TProps extends object> = (
  props: React.PropsWithoutRef<TProps>,
  ref: React.ForwardedRef<unknown>,
) => React.JSX.Element

type ComponentLibraryRefComponent<TProps extends object> = React.ElementType<TProps>

interface ComponentLibraryWrapperProps<TProps extends object> {
  renders: {
    mui: ComponentLibraryRenderer<TProps>
    nova: ComponentLibraryRenderer<TProps>
  }
  customProps: {
    mui?: React.PropsWithoutRef<Partial<TProps>>
    nova?: React.PropsWithoutRef<Partial<TProps>>
  }
  propsEnhancer?: (
    props: React.PropsWithoutRef<TProps>,
    componentLibrary: ComponentLibrary,
  ) => React.PropsWithoutRef<TProps>
  componentProps: React.PropsWithoutRef<TProps>
}

function ComponentLibraryWrapperInner<TProps extends object>(
  { renders, componentProps, propsEnhancer, customProps }: ComponentLibraryWrapperProps<TProps>,
  ref: React.ForwardedRef<unknown>,
) {
  const library = useComponentLibrary()

  return useMemo(() => {
    if (library === 'mui' && customProps.mui) {
      componentProps = { ...customProps.mui, ...componentProps }
    } else if (library === 'nova' && customProps.nova) {
      componentProps = { ...customProps.nova, ...componentProps }
    }

    if (propsEnhancer) componentProps = propsEnhancer({ ...componentProps }, library)

    return library === 'mui' ? renders.mui(componentProps, ref) : renders.nova(componentProps, ref)
  }, [library, renders, componentProps, customProps, propsEnhancer, ref])
}

const ComponentLibraryWrapper = React.forwardRef(ComponentLibraryWrapperInner) as <TProps extends object>(
  props: ComponentLibraryWrapperProps<TProps> & React.RefAttributes<unknown>,
) => React.JSX.Element

export function createComponentWrapperWithRef<TProps extends object>(
  {
    mui: MuiComponent,
    nova: NovaComponent,
    propsEnhancer,
    novaProps,
    muiProps,
    /** @deprecated Use `muiProps` instead */ muiprops,
  }: {
    mui: ComponentLibraryRefComponent<TProps>
    nova: ComponentLibraryRefComponent<TProps>
    propsEnhancer?: (
      props: React.PropsWithoutRef<TProps>,
      componentLibrary: ComponentLibrary,
    ) => React.PropsWithoutRef<TProps>
    novaProps?: React.PropsWithoutRef<Partial<TProps>>
    muiProps?: React.PropsWithoutRef<Partial<TProps>>
    muiprops?: React.PropsWithoutRef<Partial<TProps>>
  },
  displayName: string,
) {
  const StyledMuiComponent = styled(MuiComponent as React.ComponentType<any>)``
  // Nova must apply sx after its bundled theme styles, not in this empty wrapper.
  const StyledNovaComponent = styled(NovaComponent as React.ComponentType<any>, {
    skipSx: true,
    shouldForwardProp: (prop) => prop !== 'ownerState' && prop !== 'theme' && prop !== 'as',
  })``

  const WrappedComponent = React.forwardRef<unknown, TProps>(function (props, ref) {
    return (
      <ComponentLibraryWrapper
        componentProps={props}
        ref={ref}
        propsEnhancer={propsEnhancer}
        customProps={{
          mui: muiProps ?? muiprops,
          nova: novaProps,
        }}
        renders={{
          mui: (componentProps, componentRef) => {
            return React.createElement(StyledMuiComponent, { ...(componentProps as TProps), ref: componentRef })
          },
          nova: (componentProps, componentRef) => {
            return React.createElement(StyledNovaComponent, { ...(componentProps as TProps), ref: componentRef })
          },
        }}
      />
    )
  })

  if (displayName) {
    WrappedComponent.displayName = displayName
  }

  return WrappedComponent
}

export function createComponentWrapper<TProps extends object>(
  {
    mui: MuiComponent,
    nova: NovaComponent,
  }: {
    mui: React.ComponentType<TProps>
    nova: React.ComponentType<TProps>
  },
  displayName: string,
) {
  function WrapperComponent(props: TProps) {
    const library = useComponentLibrary()
    const Component = useMemo(() => (library === 'mui' ? MuiComponent : NovaComponent), [library])
    return <Component {...props} />
  }

  WrapperComponent.displayName = displayName
  return WrapperComponent
}
